import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import {
    applyResolvedAssetTimingOperation,
    hasAssetTimingArgs,
    planAssetTiming,
    resolveAssetTimingOperation,
    withTimingNote,
} from './asset-timing-placement';
import { ASSET_TIMING_SCHEMA_SHAPE } from './asset-timing-schema';
import { applyMaxHeight, suspendTextAutoResize } from './text-box-height';
import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { positionAsset } from 'js/src/agent/positioningUtils';
import { checkpointVideo } from 'js/src/data/video-data';
import { saveRawLuminary } from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setAssetGroupSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The group asset name to modify'),
    position: z
        .object({
            x: z.number().describe('X position'),
            y: z.number().describe('Y position'),
        })
        .optional()
        .describe('The group asset position as an object with x and y coordinates'),
    max_height: z
        .number()
        .min(0)
        .optional()
        .describe('The maximum height of the group asset in pixels'),
    align_children: z
        .enum(['left', 'center', 'right'])
        .optional()
        .describe('Alignment of text children in the group'),
    ...ASSET_TIMING_SCHEMA_SHAPE,
});
export type SetAssetGroupArgs = z.infer<typeof setAssetGroupSchema>;

export class SetAssetGroupTool extends VideoStateTool<typeof setAssetGroupSchema> {
    protected override readonly schema = setAssetGroupSchema;
    protected override readonly toolName = TOOL_NAMES.setGroupAsset;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Set group asset properties for a specific scene. When the user requests multiple changes (e.g., position AND max_height), include ALL requested properties in a SINGLE call. Timing here applies to the group as a whole — the group and every child appear at once, and children can then be timed later than the group (never earlier). To stagger the children so each lands as it is mentioned, time them individually, top to bottom, with ${TOOL_NAMES.setText}.`;

    public override getToolDisplayName(): string {
        return 'Setting asset group';
    }

    public get statusIcon(): string {
        return 'group_work';
    }

    public async execute(args: SetAssetGroupArgs, context: ToolExecutionContext) {
        const { asset_name, position, max_height, align_children } = args;

        const timingRequested = hasAssetTimingArgs(args);

        try {
            // Business logic validation (schema validation already done)
            if (
                position === undefined &&
                max_height === undefined &&
                align_children === undefined &&
                !timingRequested
            ) {
                return 'Error: At least one of position, max_height, align_children, start_words or start_time_ms must be provided';
            }

            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }

            if (!VideoData.isAssetGroup(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a group asset`;
            }

            const assetGroup = asset;
            const { parentActionGroup } = context;

            // Resolve timing before any edit lands. A timing-only call with bad timing fails
            // outright; alongside other edits the timing is adjusted or skipped with a note.
            const { resolveAssetTiming, applyResolvedAssetTiming } = makeOperationsCallable(
                { resolveAssetTimingOperation, applyResolvedAssetTimingOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );
            const hasPropertyEdits =
                position !== undefined || max_height !== undefined || align_children !== undefined;
            const timingPlan = timingRequested
                ? planAssetTiming(
                      resolveAssetTiming(scene, assetGroup, args),
                      args,
                      hasPropertyEdits,
                  )
                : undefined;
            if (timingPlan && 'error' in timingPlan) {
                return `Error: ${timingPlan.error}`;
            }

            const changes: string[] = [];

            try {
                // Set position if provided
                if (position !== undefined) {
                    if (typeof position !== 'object' || position === null) {
                        return 'Error: position must be an object with x and y properties';
                    }
                    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
                        return 'Error: position.x and position.y must be numbers';
                    }
                    positionAsset(assetGroup, {
                        x: position.x,
                        y: position.y,
                    });
                    changes.push(`position to (${position.x}, ${position.y})`);
                }

                // Set max_height if provided
                if (max_height !== undefined) {
                    // Otherwise the group goes on following its text and this height does nothing.
                    suspendTextAutoResize(assetGroup);
                    const heightChange = applyMaxHeight(assetGroup, max_height, 'max_height');
                    changes.push(heightChange.change);
                }

                // Set alignment of text children if provided
                if (align_children !== undefined) {
                    if (!['left', 'center', 'right'].includes(align_children)) {
                        return 'Error: align_children must be one of "left", "center", or "right"';
                    }
                    console.log('setting alignChildren', align_children);
                    assetGroup.alignChildren = align_children;
                    changes.push(`align_children to ${align_children}`);
                }

                if (timingPlan && 'skipNote' in timingPlan) {
                    changes.push(timingPlan.skipNote);
                }
                if (timingPlan && 'timing' in timingPlan) {
                    const applied = withTimingNote(
                        applyResolvedAssetTiming(
                            scene,
                            assetGroup,
                            timingPlan.timing,
                            timingPlan.args,
                            parentActionGroup,
                        ),
                        timingPlan.note,
                    );
                    changes.push('warning' in applied ? applied.warning : applied.change);
                }

                // Save changes
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                return `Success: Updated ${changes.join(', ')} for asset group ${asset_name} in scene ${sceneShortId}`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error updating asset group properties: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting asset group: ${errorMessage}`;
        }
    }
}
